import { ChangeDetectionStrategy, Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, distinctUntilChanged, map } from 'rxjs';
import { RxIntuitiveTeethDiagramComponent } from '../../components/rx-intuitive-teeth-diagram/rx-intuitive-teeth-diagram.component';
import { RxIntuitiveBridgeDiagramFooterComponent } from '../../components/rx-intuitive-bridge-diagram-footer/rx-intuitive-bridge-diagram-footer.component';
import { RxIntuitiveTeethDiagramLayoutComponent } from '../rx-intuitive-teeth-diagram-layout/rx-intuitive-teeth-diagram-layout.component';
import { RxIntuitiveTeethDiagramBridgeFacade } from '@modules/rx-intuitive-teeth-diagram/rx-intuitive-teeth-diagram-bridge.facade';
import { RxIntuitiveTeethDiagramFacade } from '../../rx-intuitive-teeth-diagram.facade';
import { RxIntuitiveTeethDiagramContainerComponent } from '../rx-intuitive-teeth-diagram-container/rx-intuitive-teeth-diagram-container.component';
import { RxIntuitiveBridgeEditorComponent } from '@modules/rx-intuitive-teeth-diagram/components/rx-intuitive-bridge-editor/rx-intuitive-bridge-editor.component';
import { RxIntuitiveBridgeIndicationButtonsComponent } from '@modules/rx-intuitive-teeth-diagram/components/rx-intuitive-bridge-indication-buttons/rx-intuitive-bridge-indication-buttons.component';
import {
	BridgeBannerCloseEvent,
	RxIntuitiveTeethDiagramBridgeBannerComponent
} from '@modules/rx-intuitive-teeth-diagram/components/rx-intuitive-bridge-banner/rx-intuitive-bridge-banner.component';
import { RxIntuitiveCopyFromBehaviorService } from '../../services/rx-intuitive-copy-from-behavior.service';
import { RxIntuitiveIndicationRulesService } from '../../services/rx-intuitive-indication-rules.service';
import { CrownForm } from '@modules/rx-intuitive-crown/models/crown-form';
import { AdditionalInfoResult } from '@modules/rx-intuitive-crown-additional-info/models/additional-info';
import { PonticDesignEnum } from '@shared/models/pontic-design.enum';
import { TlkBannerComponent } from '@itero/ui-toolkit-angular/banner';

@Component({
	selector: 'rx-intuitive-teeth-diagram-bridge-container',
	templateUrl: './rx-intuitive-teeth-diagram-bridge-container.component.html',
	styleUrls: [
		'../rx-intuitive-teeth-diagram-container/rx-intuitive-teeth-diagram-container.component.scss',
		'./rx-intuitive-teeth-diagram-bridge-container.component.scss'
	],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		RxIntuitiveTeethDiagramBridgeFacade,
		{ provide: RxIntuitiveTeethDiagramFacade, useExisting: RxIntuitiveTeethDiagramBridgeFacade }
	],
	imports: [
		CommonModule,
		TranslateModule,
		RxIntuitiveTeethDiagramComponent,
		RxIntuitiveTeethDiagramLayoutComponent,
		RxIntuitiveBridgeEditorComponent,
		RxIntuitiveBridgeIndicationButtonsComponent,
		RxIntuitiveBridgeDiagramFooterComponent,
		RxIntuitiveTeethDiagramBridgeBannerComponent,
		TlkBannerComponent
	]
})
export class RxIntuitiveTeethDiagramBridgeContainerComponent extends RxIntuitiveTeethDiagramContainerComponent {
	override readonly indications$ = this.facade.visibleIndications$;
	readonly hasUnsavedChanges$ = this.facade.hasUnsavedChanges$;
	readonly canRemoveSelectedTooth$ = combineLatest([this.facade.canRemoveSelectedTooth$, this.isTeethDiagramReadOnly$]).pipe(
		map(([canRemove, isReadOnly]) => canRemove && !isReadOnly),
		distinctUntilChanged()
	);
	readonly indicationsDisabled$ = combineLatest([this.hasSelectedTeeth$, this.isTeethDiagramReadOnly$]).pipe(
		map(([hasSelectedTeeth, isReadOnly]) => !hasSelectedTeeth || isReadOnly),
		distinctUntilChanged()
	);
	readonly showGuidanceBanner$ = this.facade.showGuidanceBanner$;
	readonly validationErrorSummary$ = this.facade.validationErrorSummary$;

	readonly bridgeEditorTeeth$ = this.facade.bridgeEditorTeeth$;
	readonly canEditBridgeEditor$ = this.facade.canEditBridgeToothEditor$;
	readonly bridgeTeethFlat$ = this.facade.selectedBridgeTeeth$;
	readonly ponticDesigns$ = this.facade.ponticDesigns$;
	readonly bridgeListItem$ = this.facade.bridgeListItem$;

	constructor(
		protected readonly facade: RxIntuitiveTeethDiagramBridgeFacade,
		copyFromBehaviorService: RxIntuitiveCopyFromBehaviorService,
		indicationRules: RxIntuitiveIndicationRulesService
	) {
		super(facade, copyFromBehaviorService, indicationRules);
	}

	onSaveBridge(): void {
		this.facade.saveBridge();
	}

	onDeleteBridge(): void {
		this.facade.deleteBridge();
	}

	onRevertChanges(): void {
		this.facade.revertChanges();
	}

	onCloseBridge(): void {
		this.facade.closeBridge();
	}

	onBridgeCrownFormChanged(crownForm: CrownForm): void {
		this.facade.updateBridgeTeethCrownData(crownForm);
	}

	onBridgeAdditionalInfoApplied(event: { toothIds: number[]; result: AdditionalInfoResult }): void {
		this.facade.updateBridgeTeethByIds(event.toothIds, event.result.values);
	}

	onBridgeClearDisabledFields(event: { toothIds: number[]; payload: Partial<Record<string, unknown>> }): void {
		this.facade.updateBridgeTeethByIds(event.toothIds, event.payload);
	}

	onRemoveToothFromBridge(): void {
		this.facade.removeToothFromBridge();
	}

	onPonticDesignChanged(event: { toothIds: number[]; ponticDesignId: PonticDesignEnum | null }): void {
		this.facade.updateBridgeTeethPonticDesign(event.toothIds, event.ponticDesignId);
	}

	onBannerClosed(event: BridgeBannerCloseEvent): void {
		this.facade.dismissGuidanceBanner(event.dontShowAgain);
	}
}
